import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { useGridTheme } from '@/config/useGridTheme';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { toast } from 'react-toastify';

export interface SqlColumn {
  columnName: string;
  type: string;
  csvHeader: string;
}

interface SftpSqlColumnMapDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: SqlColumn[]) => void;
  columns: SqlColumn[];
  destinationName: string;
}

interface SqlColumnMapRow extends SqlColumn {
  id: string; // For AG Grid row tracking
}

// SQL Data Types from the MCP tool
const SQL_DATA_TYPES = [
  'STRING',
  'INT',
  'DECIMAL',
  'BOOLEAN',
  'DATE_TIME',
  'GUID',
  'LONG',
  'SHORT',
  'BYTE',
  'DOUBLE',
  'FLOAT',
];

// Delete button cell renderer
const DeleteButtonRenderer: React.FC<ICellRendererParams> = (props) => {
  const handleDelete = () => {
    if (props.context?.deleteRow) {
      props.context.deleteRow(props.node.data.id);
    }
  };

  return (
    <Button
      size="small"
      color="error"
      variant="contained"
      startIcon={<DeleteIcon />}
      onClick={handleDelete}
      sx={{ 
        minWidth: 'auto',
        fontSize: '0.8125rem',
        py: 0.5,
        px: 1.5
      }}
    >
      Delete
    </Button>
  );
};

export const SftpSqlColumnMapDialog: React.FC<SftpSqlColumnMapDialogProps> = ({
  open,
  onClose,
  onSave,
  columns,
  destinationName,
}) => {
  const gridRef = useRef<AgGridReact<SqlColumnMapRow>>(null);
  const gridTheme = useGridTheme();
  const [rowData, setRowData] = useState<SqlColumnMapRow[]>([]);
  const [originalData, setOriginalData] = useState<SqlColumnMapRow[]>([]);
  const [nextId, setNextId] = useState(1);

  // Initialize data from props
  useEffect(() => {
    if (open && columns) {
      const mappedData = columns.map((col, index) => ({
        ...col,
        id: `existing-${index}`,
      }));
      setRowData(mappedData);
      setOriginalData(mappedData);
      setNextId(mappedData.length + 1);
    }
  }, [columns, open]);

  // Column definitions
  const columnDefs = useMemo<ColDef<SqlColumnMapRow>[]>(() => [
    {
      field: 'columnName',
      headerName: 'Column Name',
      editable: true,
      flex: 1,
      cellDataType: 'text',
    },
    {
      field: 'type',
      headerName: 'Data Type',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: SQL_DATA_TYPES,
      },
      flex: 1,
    },
    {
      field: 'csvHeader',
      headerName: 'CSV Header',
      editable: true,
      flex: 1,
      cellDataType: 'text',
    },
    {
      headerName: '',
      width: 100,
      cellRenderer: DeleteButtonRenderer,
      editable: false,
      sortable: false,
      filter: false,
    },
  ], []);

  // Grid context for cell renderers
  const gridContext = useMemo(() => ({
    deleteRow: (id: string) => {
      setRowData(prev => prev.filter(row => row.id !== id));
    },
  }), []);

  // Add new row
  const handleAddRow = () => {
    const newRow: SqlColumnMapRow = {
      id: `new-${nextId}`,
      columnName: '',
      type: 'STRING',
      csvHeader: '',
    };
    setRowData(prev => [...prev, newRow]);
    setNextId(prev => prev + 1);
  };

  // Validate data
  const validateData = (): string[] => {
    const errors: string[] = [];
    const columnNames = new Set<string>();
    const csvHeaders = new Set<string>();

    rowData.forEach((row, index) => {
      if (!row.columnName.trim()) {
        errors.push(`Row ${index + 1}: Column Name is required`);
      } else if (columnNames.has(row.columnName)) {
        errors.push(`Row ${index + 1}: Duplicate Column Name "${row.columnName}"`);
      } else {
        columnNames.add(row.columnName);
      }

      if (!row.csvHeader?.trim()) {
        errors.push(`Row ${index + 1}: CSV Header is required`);
      } else if (csvHeaders.has(row.csvHeader)) {
        errors.push(`Row ${index + 1}: Duplicate CSV Header "${row.csvHeader}"`);
      } else {
        csvHeaders.add(row.csvHeader);
      }

      if (!row.type) {
        errors.push(`Row ${index + 1}: Data Type is required`);
      }
    });

    return errors;
  };

  // Save changes
  const handleSave = () => {
    // Stop any active cell editing and commit the changes
    if (gridRef.current?.api) {
      gridRef.current.api.stopEditing();
    }

    // Validate data
    const errors = validateData();
    if (errors.length > 0) {
      toast.error(
        <Box>
          <Typography variant="subtitle2">Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Box>
      );
      return;
    }

    // Prepare data (remove id field)
    const sqlColumns = rowData.map(({ id, ...col }) => col);
    onSave(sqlColumns);
    onClose();
  };

  // Handle close
  const handleClose = () => {
    // Reset to original data
    setRowData(originalData);
    onClose();
  };

  // Check if data has changed
  const hasChanges = useMemo(() => {
    if (rowData.length !== originalData.length) return true;
    
    return rowData.some((row, index) => {
      const original = originalData[index];
      return !original || 
        row.columnName !== original.columnName ||
        row.type !== original.type ||
        row.csvHeader !== original.csvHeader;
    });
  }, [rowData, originalData]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        SQL Column Mapping - {destinationName}
      </DialogTitle>
      <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define how CSV columns map to SQL table columns. All mappings will be saved together.
            </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                size="small"
                sx={{ mt: 1 }}
              >
                Add Column Mapping
              </Button>
            </Box>

            {rowData.length === 0 ? (
              <Alert severity="info">
                No column mappings defined. Click "Add Column Mapping" to create one.
              </Alert>
            ) : (
              <Box 
                sx={{ 
                  height: 400, 
                  width: '100%',
                  '& .ag-cell': {
                    cursor: 'text',
                  },
                  '& .ag-cell-focus': {
                    border: '1px solid #1976d2 !important',
                  }
                }} 
                >
                <AgGridReact<SqlColumnMapRow>
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  context={gridContext}
                  theme={gridTheme}
                  onCellValueChanged={(event) => {
                    // Update local state when cell value changes
                    const updatedData = [...rowData];
                    const index = updatedData.findIndex(row => row.id === event.data.id);
                    if (index !== -1) {
                      updatedData[index] = event.data;
                      setRowData(updatedData);
                    }
                  }}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    editable: true,
                  }}
                  animateRows={true}
                  rowSelection={{
                    mode: 'singleRow',
                    checkboxes: false,
                    enableClickSelection: true
                  }}
                  getRowId={(params) => params.data.id}
                  singleClickEdit={true}
                />
              </Box>
            )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Note: Default values and transformations will be available in a future update.
          </Alert>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          startIcon={<CancelIcon />}
          disabled={false}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!hasChanges}
        >
          Save All Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};